import React from "react";

export const CHART_INK = "#172033";
export const CHART_MUTED = "#7b879c";
export const CHART_GRID = "#eceff5";
export const CHART_FONT = "Inter, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";

export const PORTAL_COLORS = {
	qbot: "#22419e",
	paycentral: "#0e7490",
	kyc: "#6d28d9",
	tripola: "#b45309",
	unknown: "#94a3b8",
};

export const STATUS_COLORS = {
	success: "#0f766e",
	paid: "#0f766e",
	active: "#0f766e",
	failed: "#be123c",
	expired: "#be123c",
	refunded: "#b45309",
	pending: "#b45309",
	neutral: "#64748b",
};

export const SERIES_COLORS = [
	"#22419e",
	"#0e7490",
	"#6d28d9",
	"#b45309",
	"#0f766e",
	"#be123c",
];

export const portalColor = (name) =>
	PORTAL_COLORS[String(name || "").toLowerCase().replace(/\s+/g, "")] ||
	PORTAL_COLORS.unknown;

export const statusColor = (name) =>
	STATUS_COLORS[String(name || "").toLowerCase()] || STATUS_COLORS.neutral;

export const withAlpha = (hex, alpha) => {
	const value = String(hex).replace("#", "");
	const full =
		value.length === 3
			? value
					.split("")
					.map((char) => char + char)
					.join("")
			: value;
	const int = parseInt(full, 16);
	const r = (int >> 16) & 255;
	const g = (int >> 8) & 255;
	const b = int & 255;
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const formatCompact = (value) => {
	const numeric = Number(value || 0);
	if (Math.abs(numeric) >= 1000) {
		return new Intl.NumberFormat("en", {
			notation: "compact",
			maximumFractionDigits: 1,
		}).format(numeric);
	}
	return new Intl.NumberFormat("en", { maximumFractionDigits: 2 }).format(
		numeric
	);
};

export const formatFull = (value) =>
	new Intl.NumberFormat("en", { maximumFractionDigits: 2 }).format(
		Number(value || 0)
	);

/* ---------- Chart.js ---------- */

const areaFill = (color) => (context) => {
	const { ctx, chartArea } = context.chart;
	if (!chartArea) return withAlpha(color, 0.14);
	const gradient = ctx.createLinearGradient(
		0,
		chartArea.top,
		0,
		chartArea.bottom
	);
	gradient.addColorStop(0, withAlpha(color, 0.26));
	gradient.addColorStop(1, withAlpha(color, 0));
	return gradient;
};

const tooltipStyle = (formatter = formatFull) => ({
	backgroundColor: "#0f172a",
	titleColor: "#e2e8f5",
	bodyColor: "#ffffff",
	borderColor: "rgba(255,255,255,0.08)",
	borderWidth: 1,
	padding: 12,
	cornerRadius: 10,
	displayColors: false,
	titleFont: { family: CHART_FONT, size: 11, weight: "600" },
	bodyFont: { family: CHART_FONT, size: 13, weight: "700" },
	callbacks: {
		label: (context) => formatter(context.parsed.y ?? context.parsed),
	},
});

const categoryScale = {
	grid: { display: false },
	border: { display: false },
	ticks: {
		color: CHART_MUTED,
		font: { family: CHART_FONT, size: 11, weight: "500" },
		padding: 8,
	},
};

const valueScale = (formatter = formatCompact) => ({
	beginAtZero: true,
	grid: { color: CHART_GRID, drawTicks: false },
	border: { display: false },
	ticks: {
		color: CHART_MUTED,
		font: { family: CHART_FONT, size: 11, weight: "500" },
		padding: 10,
		maxTicksLimit: 5,
		callback: (value) => formatter(value),
	},
});

export const areaSeries = ({ label, data, color = PORTAL_COLORS.qbot }) => ({
	label,
	data,
	borderColor: color,
	backgroundColor: areaFill(color),
	borderWidth: 2,
	fill: true,
	tension: 0.35,
	pointRadius: 0,
	pointHoverRadius: 5,
	pointBackgroundColor: "#ffffff",
	pointBorderColor: color,
	pointBorderWidth: 2,
	pointHitRadius: 18,
});

export const barSeries = ({ label, data, colors }) => ({
	label,
	data,
	backgroundColor: colors,
	hoverBackgroundColor: colors,
	borderWidth: 0,
	borderRadius: 8,
	borderSkipped: false,
	maxBarThickness: 42,
});

export const ringSeries = ({ data, colors }) => ({
	data,
	backgroundColor: colors,
	hoverBackgroundColor: colors,
	borderColor: "#ffffff",
	borderWidth: 3,
	hoverOffset: 6,
	spacing: 1,
});

export const lineChartOptions = (formatter = formatFull) => ({
	responsive: true,
	maintainAspectRatio: false,
	interaction: { mode: "index", intersect: false },
	plugins: { legend: { display: false }, tooltip: tooltipStyle(formatter) },
	scales: { x: categoryScale, y: valueScale() },
});

export const barChartOptions = (formatter = formatFull) => ({
	responsive: true,
	maintainAspectRatio: false,
	plugins: { legend: { display: false }, tooltip: tooltipStyle(formatter) },
	scales: { x: categoryScale, y: valueScale() },
});

export const ringChartOptions = (formatter = formatFull) => ({
	responsive: true,
	maintainAspectRatio: false,
	cutout: "72%",
	plugins: { legend: { display: false }, tooltip: tooltipStyle(formatter) },
});

/* Ring charts carry their total in the hole instead of a label ring. */
export const ringCenterPlugin = {
	id: "reportRingCenter",
	afterDatasetsDraw(chart) {
		const { ctx, chartArea } = chart;
		if (!chartArea) return;

		const caption = chart.options?.plugins?.reportRingCenter?.caption;
		if (!caption) return;

		const total = (chart.data?.datasets?.[0]?.data || []).reduce(
			(sum, value) => sum + Number(value || 0),
			0
		);
		const centerX = (chartArea.left + chartArea.right) / 2;
		const centerY = (chartArea.top + chartArea.bottom) / 2;

		ctx.save();
		ctx.textAlign = "center";
		ctx.fillStyle = CHART_INK;
		ctx.font = `700 24px ${CHART_FONT}`;
		ctx.fillText(formatCompact(total), centerX, centerY + 2);
		ctx.fillStyle = CHART_MUTED;
		ctx.font = `500 11px ${CHART_FONT}`;
		ctx.fillText(caption, centerX, centerY + 22);
		ctx.restore();
	},
};

export const legendFromChartData = (chartData) => {
	const dataset = chartData?.datasets?.[0] || {};
	return (chartData?.labels || []).map((label, index) => ({
		label,
		value: Number(dataset.data?.[index] || 0),
		color:
			(Array.isArray(dataset.backgroundColor)
				? dataset.backgroundColor[index]
				: dataset.backgroundColor) || CHART_MUTED,
	}));
};

/* ---------- Recharts ---------- */

export const rechartsAxis = {
	axisLine: false,
	tickLine: false,
	tick: { fill: CHART_MUTED, fontSize: 11 },
};

export const rechartsGrid = {
	stroke: CHART_GRID,
	strokeDasharray: "4 6",
	vertical: false,
};

export const AreaGradient = ({ id, color }) => (
	<defs>
		<linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
			<stop offset="0%" stopColor={color} stopOpacity={0.26} />
			<stop offset="100%" stopColor={color} stopOpacity={0} />
		</linearGradient>
	</defs>
);

export const ReportTooltip = ({
	active,
	payload,
	label,
	formatter = formatFull,
}) => {
	if (!active || !payload?.length) return null;

	const entry = payload[0];
	return (
		<div className="report-chart-tip">
			<p className="report-chart-tip-label">{label ?? entry.name}</p>
			<p className="report-chart-tip-value">{formatter(entry.value)}</p>
		</div>
	);
};

export const RingCenter = ({ viewBox, caption, value }) => {
	const { cx, cy } = viewBox || {};
	if (cx == null || cy == null) return null;

	return (
		<g>
			<text x={cx} y={cy + 2} textAnchor="middle" className="report-ring-value">
				{value}
			</text>
			<text
				x={cx}
				y={cy + 22}
				textAnchor="middle"
				className="report-ring-caption"
			>
				{caption}
			</text>
		</g>
	);
};

/* ---------- Shared panel chrome ---------- */

export const ChartPanel = ({
	title,
	caption,
	action,
	height = 320,
	loading = false,
	isEmpty = false,
	emptyLabel = "No data for the selected filters",
	children,
}) => (
	<section className="report-panel">
		<header className="report-panel-head">
			<div>
				<h3>{title}</h3>
				{caption ? <p>{caption}</p> : null}
			</div>
			{action}
		</header>

		<div className="report-panel-body" style={{ height }}>
			{loading ? (
				<div className="report-panel-skeleton" aria-hidden="true" />
			) : isEmpty ? (
				<p className="report-panel-empty">{emptyLabel}</p>
			) : (
				children
			)}
		</div>
	</section>
);

export const ChartLegend = ({ items = [], formatter = formatFull }) => {
	const total = items.reduce((sum, item) => sum + Number(item.value || 0), 0);

	return (
		<ul className="report-legend">
			{items.map((item) => (
				<li key={item.label}>
					<span
						className="report-legend-dot"
						style={{ backgroundColor: item.color }}
						aria-hidden="true"
					/>
					<span className="report-legend-label">{item.label}</span>
					<span className="report-legend-value">{formatter(item.value)}</span>
					<span className="report-legend-share">
						{total
							? `${Math.round((Number(item.value || 0) / total) * 100)}%`
							: "0%"}
					</span>
				</li>
			))}
		</ul>
	);
};
